/**
 * Fixture-only custom Tours flow.
 *
 * `/preview` must never quote, reserve, or charge against a real project.
 * This module deliberately creates a short-lived in-memory experience and a
 * preview booking that existing checkout code recognizes by its ID prefix.
 * The production edge function remains the authority for road distance,
 * national validation, inventory, and money.
 */
import { queryClient } from '../lib/query-client'
import { toursQueryKeys } from '../lib/hooks/use-tours'
import { MIN_TOUR_PASSENGERS } from '../lib/tours/booking-rules'
import {
  grossToHostNetAtOrAbove,
  hostNetToGross,
  toursPerPersonFromVehicleCents,
} from '../lib/tours/pricing'
import { scoreCustomTourQuoteModel } from '../lib/tours/custom-quote-model'
import type {
  TourCustomQuoteInput,
  TourCustomQuoteResult,
  TourExperienceDetail,
  TourReserveInput,
  TourReserveResult,
  TourVehicleOption,
} from '../lib/types/tours.types'
import { previewCatalog } from './data'

const PREVIEW_QUOTE_TTL_MS = 60 * 60 * 1000
const PREVIEW_VEHICLES: Record<
  TourCustomQuoteInput['vehicleType'],
  Pick<TourVehicleOption, 'unitId' | 'label' | 'seats' | 'cargoVolumeM3'>
> = {
  microbus: {
    unitId: 'tourunit_preview_custom_microbus',
    label: 'Microbús',
    seats: 15,
    cargoVolumeM3: 1.5,
  },
  coaster: {
    unitId: 'tourunit_preview_custom_coaster',
    label: 'Coaster',
    seats: 26,
    cargoVolumeM3: 3.2,
  },
  bus: {
    unitId: 'tourunit_preview_custom_bus',
    label: 'Bus',
    seats: 45,
    cargoVolumeM3: 5.5,
  },
}

let quoteSequence = 0
const previewBookings = new Map<string, TourReserveResult>()

function haversineKm(
  latitudeA: number,
  longitudeA: number,
  latitudeB: number,
  longitudeB: number,
): number {
  const radians = (value: number) => (value * Math.PI) / 180
  const earthRadiusKm = 6371
  const deltaLatitude = radians(latitudeB - latitudeA)
  const deltaLongitude = radians(longitudeB - longitudeA)
  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(radians(latitudeA)) * Math.cos(radians(latitudeB)) * Math.sin(deltaLongitude / 2) ** 2
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** A visual-only road-distance proxy for isolated fixture QA. */
function previewRoadDistanceKm(input: TourCustomQuoteInput): number {
  const points = [input.origin, ...input.stops, input.destination]
  let directKm = 0
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]
    const to = points[index]
    if (!from || !to) continue
    directKm += haversineKm(from.latitude, from.longitude, to.latitude, to.longitude)
  }
  // Roads in El Salvador rarely follow a perfect chord. This factor is solely
  // for visual QA; production obtains the exact road route from Mapbox.
  return Number(Math.min(300, Math.max(2, directKm * 1.28)).toFixed(2))
}

function previewTravelInfo(origin: string): NonNullable<TourExperienceDetail['travelInfo']> {
  return {
    meetingPoint: {
      label: origin,
      note: 'Confirmá el punto exacto con tu conductor antes de salir.',
    },
    whatToBring: ['Identificación vigente', 'Agua para el recorrido', 'Lo necesario para el grupo'],
    documents: {
      required: ['DUI o documento de identidad vigente'],
      note: 'Este recorrido de vista previa permanece dentro de El Salvador.',
    },
    policies: [
      'La cotización se guarda por una hora y se confirma al completar la reserva.',
      'La salida se programa con la anticipación mínima aplicable a Tours.',
    ],
    legalNotice: 'Vista previa aislada: no crea una reserva ni un pago real.',
    isInternational: false,
  }
}

function getPreviewExperience(id: string): TourExperienceDetail | null {
  const value = queryClient.getQueryData(toursQueryKeys.experience(id))
  if (!value || typeof value !== 'object') return null
  return value as TourExperienceDetail
}

export async function previewCreateCustomTourQuote(
  input: TourCustomQuoteInput,
): Promise<TourCustomQuoteResult> {
  const vehicle = PREVIEW_VEHICLES[input.vehicleType]
  if (
    !vehicle ||
    input.passengerCount < MIN_TOUR_PASSENGERS ||
    input.passengerCount > vehicle.seats
  ) {
    throw Object.assign(new Error('Selected vehicle cannot fit the group'), {
      code: 'TOURS_CAPACITY_EXCEEDED',
    })
  }

  const distanceKm = previewRoadDistanceKm(input)
  const points = [input.origin, ...input.stops, input.destination]
  const model = scoreCustomTourQuoteModel({
    vehicleType: input.vehicleType,
    vehicleCapacity: vehicle.seats,
    stopCount: points.length,
    distanceKm,
  })
  const guestPriceCents = hostNetToGross(grossToHostNetAtOrAbove(model.safePriceCents))
  const perPersonCents = toursPerPersonFromVehicleCents(guestPriceCents, input.passengerCount)
  const expiresAt = new Date(Date.now() + PREVIEW_QUOTE_TTL_MS).toISOString()
  const id = `tourexp_preview_custom_${++quoteSequence}`
  const quoteId = `tourquote_preview_custom_${quoteSequence}`
  const durationMinutes = Math.min(
    960,
    Math.max(60, Math.ceil((distanceKm / 35) * 60) + 30 + input.stops.length * 20),
  )

  const detail: TourExperienceDetail = {
    id,
    title: 'Tu recorrido personalizado',
    description: `Recorrido privado nacional: ${input.origin.label} → ${input.destination.label}.`,
    originLabel: input.origin.label,
    destinationLabel: input.destination.label,
    durationMinutes,
    distanceKm,
    coverImageUrl: previewCatalog[0]?.coverImageUrl ?? null,
    originCountryCode: 'SV',
    destinationCountryCode: 'SV',
    currency: 'USD',
    host: { displayName: 'Tago', businessName: 'Tago Rent' },
    vehicles: [{ ...vehicle, guestPriceCents }],
    travelInfo: previewTravelInfo(input.origin.label),
    customQuote: {
      id: quoteId,
      expiresAt,
      isExpired: false,
      guestPriceCents,
      passengerCount: input.passengerCount,
      vehicleType: input.vehicleType,
    },
    stops: points.map((point, position) => ({
      id: `${id}_stop_${position}`,
      position,
      label: point.label,
      latitude: point.latitude,
      longitude: point.longitude,
      kind: position === 0 ? 'pickup' : position === points.length - 1 ? 'destination' : 'waypoint',
    })),
  }

  queryClient.setQueryData(toursQueryKeys.experience(id), detail)
  return {
    quoteId,
    experienceId: id,
    expiresAt,
    reused: false,
    guestPriceCents,
    perPersonCents,
    totalCents: perPersonCents * input.passengerCount,
    currency: 'USD',
    distanceKm,
    durationMinutes,
    passengerCount: input.passengerCount,
    vehicle: { unitId: vehicle.unitId, label: vehicle.label, seats: vehicle.seats },
  }
}

export async function previewReserveCustomTour(
  input: TourReserveInput,
): Promise<TourReserveResult> {
  const existing = previewBookings.get(input.idempotencyKey)
  if (existing) return { ...existing, reused: true }

  const experience = getPreviewExperience(input.experienceId)
  const quote = experience?.customQuote
  const vehicle = experience?.vehicles.find((item) => item.unitId === input.unitId)
  if (
    !experience ||
    !quote ||
    quote.isExpired ||
    new Date(quote.expiresAt).getTime() <= Date.now()
  ) {
    throw Object.assign(new Error('Custom quote has expired'), { code: 'TOURS_NOT_FOUND' })
  }
  if (
    !vehicle ||
    input.passengerCount !== quote.passengerCount ||
    input.passengerCount > vehicle.seats
  ) {
    throw Object.assign(new Error('Custom quote capacity is invalid'), {
      code: 'TOURS_CAPACITY_EXCEEDED',
    })
  }

  const result: TourReserveResult = {
    booking: {
      id: `tourbook_preview_custom_${input.idempotencyKey}`,
      departureId: `tourdeparture_preview_custom_${input.idempotencyKey}`,
      organizerId: 'preview',
      passengerCount: input.passengerCount,
      cargoM3: 0,
      luggageSpec: input.luggageSpec,
      holdLockId: `tourlock_preview_custom_${input.idempotencyKey}`,
      holdExpiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      totalCents:
        toursPerPersonFromVehicleCents(vehicle.guestPriceCents, input.passengerCount) *
        input.passengerCount,
      guestPriceCents: vehicle.guestPriceCents,
      startsAt: input.startsAt,
      unitId: input.unitId,
      status: 'capacity_held',
    },
    reused: false,
  }
  previewBookings.set(input.idempotencyKey, result)
  return result
}
